import { FieldMapShape } from './field-map-shape';
import * as L from 'leaflet';
import { LeafletEvent } from 'leaflet';
import { Block } from './block';
import * as turf from '@turf/turf';
import { Plot } from './plot';
import { convertPolygonToTurf, convertTurfToPolygon, deepCloneLatLngs, featureToL, updateStylePolygon } from './shape-utils';
import { FieldMapContext } from '../field-map-context.model';

export class Study extends FieldMapShape {
    id = '';
    isEditing = false;
    isSelected = false;
    name = '';
    polygon: L.Polygon | null = null;
    parentBlock: Block | null = null;
    editInitialLatLng?: L.LatLng[][];
    type: 'STUDY' = 'STUDY';
    turfBoundingBoxPolygon?: turf.helpers.Feature<turf.helpers.Polygon, {}>;
    isOutsideBoundary?: boolean;
    plots: Map<string, Plot> = new Map<string, Plot>();
    plotsDrawingLayer: L.FeatureGroup<L.Polygon>;
    crop: string;
    environmentNumber: string;
    private editablePolygon: L.Polygon;
    private editablePlotsTurf: turf.helpers.FeatureCollection<turf.helpers.Polygon>;
    private editablePlotsLayer: L.FeatureGroup;
    private originalPlotsDrawingLayer: L.FeatureGroup;

    constructor(params: {
        map: L.Map,
        id: string,
        name: string,
        crop: string,
        polygon: L.Polygon,
        parent: Block,
        fieldMapContext: FieldMapContext,
        environmentNumber: string;
    }) {
        super(params.map);
        this.id = params.id;
        this.name = params.name;
        this.polygon = params.polygon;
        this.parentBlock = params.parent;
        this.fieldMapContext = params.fieldMapContext;
        this.crop = params.crop;
        this.environmentNumber = params.environmentNumber;
        this.initialize();
    }

    initialize() {
        this.plotsDrawingLayer = L.featureGroup().addTo(this.map);
        if (this.parentBlock) {
            this.parentBlock.studies.push(this);
        }
        super.initialize();
    }

    draw(): void {
        this.fieldMapContext.studiesMap.set(this.id, this);

        if (this.parentBlock) {
            this.parentBlock.studiesDrawingLayer.addLayer(this.polygon);
        } else {
            this.map.addLayer(this.polygon);
        }
        this.polygon.remove();

        // Draw the plots inside the study
        // When a Plot is drawn, it will add a polygon layer to plotsDrawingLayer
        this.plots.forEach((plot) => {
            plot.draw();
        });

        // After the plots are drawn,
        // Get the convex of the plots and draw a polygon that wraps around the plots area.
        this.polygon = L.polygon(featureToL(turf.transformScale(turf.convex(this.plotsDrawingLayer.toGeoJSON() as any), 1.125)));

        if (this.parentBlock) {
            this.parentBlock.studiesDrawingLayer.addLayer(this.polygon);
        } else {
            this.map.addLayer(this.polygon);
        }

        this.plotsDrawingLayer.bringToFront();

        // Re-add listeners since the polygon is recreated.
        this.addListeners();

        this.updateStyle('DEFAULT');

        super.draw();
    }

    async commit(): Promise<void> {
        return this.fieldMapContext.beforeShapeCommit(this).then(() => {
            if (this.editablePolygon && this.editablePlotsLayer) {
                // Create new plotsDrawing Layer
                this.plotsDrawingLayer = L.featureGroup();
                this.plotsDrawingLayer.addTo(this.map);

                // Update the polygon of each plot
                this.editablePlotsTurf.features.forEach((feature) => {
                    const plot = this.plots.get(feature.properties.observationUnit.observationUnitDbId);
                    plot.polygon = convertTurfToPolygon(feature);
                });
                this.originalPlotsDrawingLayer = null;
                this.resetEdit();
                this.draw();
            }
            this.fieldMapContext.enableMouseEventListeners();

            this.fieldMapContext.afterShapeCommit(this);

            return Promise.resolve();
        }, () => {
            return Promise.reject();
        });
    }

    reset(): void {
        if (this.editablePolygon && this.editablePlotsLayer) {
            this.plotsDrawingLayer = L.featureGroup();
            this.plotsDrawingLayer.addTo(this.map);

            this.originalPlotsDrawingLayer.eachLayer((layer: L.Layer) => {
                const plotPolygon = layer as L.Polygon;
                const plot = this.plots.get(plotPolygon.feature.properties.observationUnit.observationUnitDbId);
                plot.polygon = plotPolygon;
            });

            this.editablePolygon.transform.disable();
            this.editablePolygon.dragging.disable();
            this.editablePlotsLayer.remove();
            this.editablePolygon.remove();
            this.editablePolygon = null;
            this.editablePlotsLayer = null;
            this.editablePlotsTurf = null;
            this.isEditing = false;
            this.originalPlotsDrawingLayer = null;
            this.draw();
        }
        this.fieldMapContext.enableMouseEventListeners();
    }

    private storeOriginalPlotsDrawingLayer() {
        if (!this.originalPlotsDrawingLayer) {
            this.originalPlotsDrawingLayer = L.featureGroup();
            this.plotsDrawingLayer.eachLayer((layer: L.Layer) => {
                const polygon = layer as L.Polygon;
                const latLngCopy = deepCloneLatLngs(polygon.getLatLngs());
                const newPolygon = L.polygon(latLngCopy);
                newPolygon.feature = polygon.feature;
                this.originalPlotsDrawingLayer.addLayer(newPolygon);
            });
            // this.originalPlotsDrawingLayer.addTo(this.map).setStyle({color: 'white'});
        }
    }

    resetEdit(): void {
        if (this.editablePolygon && this.editablePlotsLayer) {
            // Create new plotsDrawing Layer
            this.plotsDrawingLayer = L.featureGroup();
            this.plotsDrawingLayer.addTo(this.map);

            // Update the polygon of each plot
            this.editablePlotsTurf.features.forEach((feature) => {
                const plot = this.plots.get(feature.properties.observationUnit.observationUnitDbId);
                plot.polygon = convertTurfToPolygon(feature);
                this.plotsDrawingLayer.addLayer(plot.polygon);
            });
            this.editablePolygon.transform.disable();
            this.editablePolygon.dragging.disable();
            this.editablePlotsLayer.remove();
            this.editablePolygon.remove();
            this.editablePolygon = null;
            this.editablePlotsLayer = null;
            this.editablePlotsTurf = null;
        }
        this.isEditing = false;
        // this.plots.forEach((plot) => {
        //     plot.reset();
        // });
        this.updateStyle('DEFAULT');
    }

    edit(): void {
        // this.isEditing = true;
        // this.plots.forEach((plot) => {
        //     plot.edit();
        // });
        // this.updateStyle('DEFAULT');
    }

    resize() {

        this.fieldMapContext.disableMouseEventListeners();
        this.storeOriginalPlotsDrawingLayer();
        this.resetEdit();

        const dragStart = (e: L.LeafletEvent) => {
            if (this.editablePlotsLayer) {
                this.editablePlotsLayer.remove();
            }
        };

        const dragEnd = (e: L.DragEndEvent) => {
            const target = e.target;
            const startPos = turf.center(this.editablePlotsTurf);
            const endPos = turf.center(target.toGeoJSON());
            this.editablePlotsTurf = turf.transformTranslate(this.editablePlotsTurf,
                turf.distance(startPos, endPos),
                turf.bearing(startPos, endPos));

            // When the dragging ends, redraw the editablePlotsLayer
            this.editablePlotsLayer = L.featureGroup(this.editablePlotsTurf.features.map((o) => {
                const polygon = convertTurfToPolygon(o);
                updateStylePolygon(polygon, 'PLOT', 'DEFAULT');
                return polygon;
            }));

            if (this.parentBlock) {
                const draggedPolygonTurf = turf.convex(this.editablePlotsLayer.toGeoJSON() as any);
                const turfBoundingPolygon = convertPolygonToTurf(this.parentBlock.polygon);
                if (!turf.booleanWithin(draggedPolygonTurf, turfBoundingPolygon)) {
                    this.parentBlock.updateStyle('BOUNDARY_ERROR');
                    this.isOutsideBoundary = true;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                } else {
                    this.parentBlock.updateStyle('DEFAULT');
                    this.isOutsideBoundary = false;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                }
            }

            this.editablePlotsLayer.addTo(this.map).bringToBack();

        };

        const scaleStart = (e: L.LeafletEvent) => {
            if (this.editablePlotsLayer) {
                this.editablePlotsLayer.remove();
            }
        };

        const createEditablePolygon = (convexArea: L.FeatureGroup<any>) => {
            const editablePolygon = L.polygon(featureToL(turf.convex(convexArea.toGeoJSON() as any)));
            editablePolygon.transform = new L.Handler.PathTransform(editablePolygon);
            editablePolygon.dragging = new L.Handler.PathDrag(editablePolygon);
            editablePolygon.on('dragstart', dragStart);
            editablePolygon.on('dragend', dragEnd);
            editablePolygon.on('scalestart', scaleStart);
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            editablePolygon.on('scaleend', scaleEnd);
            return editablePolygon;
        };

        const scaleEnd = (e: LeafletEvent) => {

            const target: L.Polygon = e.target as L.Polygon;
            const startPos = turf.center(this.editablePlotsTurf);
            const endPos = turf.center(target.toGeoJSON());
            const startArea = turf.area(this.editablePlotsTurf);
            const endArea = turf.area(target.toGeoJSON());
            const factor = Math.sqrt(endArea / startArea);

            // Resize the plots inside the study area
            this.editablePlotsTurf = turf.featureCollection(this.editablePlotsTurf.features.map((plot) => {
                const startCoord = turf.getCoords(startPos);
                const plotCoord = turf.getCoords(turf.center(plot));
                const bearing = turf.bearing(startCoord, plotCoord);
                const distance = turf.distance(startCoord, plotCoord);
                // after resize, bearing to centroid of all plots is the same, but scaled by the resize factor
                const plotEndCoord = turf.getCoords(turf.destination(turf.getCoords(endPos), distance * factor, bearing));
                plot = turf.transformTranslate(plot,
                    turf.distance(plotCoord, plotEndCoord),
                    turf.bearing(plotCoord, plotEndCoord));
                plot = turf.transformScale(plot, factor);
                return plot;
            }));

            // When the scaling/resizing ends, redraw the editablePlotsLayer
            this.editablePlotsLayer = L.featureGroup(this.editablePlotsTurf.features.map((o) => {
                const polygon = convertTurfToPolygon(o);
                updateStylePolygon(polygon, 'PLOT', 'DEFAULT');
                return polygon;
            }));
            this.editablePlotsLayer.addTo(this.map);

            // Remove and disable transformation of the current editablePolygon, we will recreate editablePolygon later
            this.editablePolygon.transform.disable();
            this.editablePolygon.dragging.disable();
            this.editablePolygon.remove();

            // Recreate the editable polygon so that the resize calculation is reset after the plot shapes are transformed.
            // scale area is now based on the updated editablePlotsLayer
            this.editablePolygon = createEditablePolygon(this.editablePlotsLayer);
            this.editablePolygon.addTo(this.map).bringToFront();
            updateStylePolygon(this.editablePolygon, 'STUDY', 'EDIT');
            this.editablePolygon.transform.enable({ scaling: true, rotation: false });
            this.editablePolygon.dragging.enable();

            if (this.parentBlock) {
                const scaledPolygonTurf = turf.convex(this.editablePlotsLayer.toGeoJSON() as any);
                const turfBoundingPolygon = convertPolygonToTurf(this.parentBlock.polygon);
                if (!turf.booleanWithin(scaledPolygonTurf, turfBoundingPolygon)) {
                    this.parentBlock.updateStyle('BOUNDARY_ERROR');
                    this.isOutsideBoundary = true;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                } else {
                    this.parentBlock.updateStyle('DEFAULT');
                    this.isOutsideBoundary = false;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                }
            }

        };

        this.polygon.remove();
        this.plotsDrawingLayer.remove();

        // Create a temporary editable polygon
        this.editablePolygon = createEditablePolygon(this.plotsDrawingLayer);

        // Create a temporary editablePlots layer that will be used to draw the transformed shape.
        // Copy the shapes from the main plotsDrawingLayer
        this.editablePlotsLayer = L.featureGroup();
        this.plotsDrawingLayer.eachLayer((layer: L.Layer) => {
            const polygon = L.polygon((layer as L.Polygon).getLatLngs());
            updateStylePolygon(polygon, 'PLOT', 'EDIT');
            this.editablePlotsLayer.addLayer(polygon);
        });

        // Create a feature collection that will be used to track/stora the transformed shapes.
        this.editablePlotsTurf = turf.featureCollection(Array.from(this.plots.values()).map((plot: any) => convertPolygonToTurf(plot.polygon)));

        this.editablePlotsLayer.addTo(this.map);
        this.editablePolygon.addTo(this.map);
        this.editablePolygon.transform.enable({ scaling: true, rotation: false });
        this.editablePolygon.dragging.enable();

        this.isEditing = true;
        updateStylePolygon(this.editablePolygon, this.type, 'EDIT'); // Highlight selected polygon

    }

    rotate() {

        this.fieldMapContext.disableMouseEventListeners();
        this.storeOriginalPlotsDrawingLayer();
        this.resetEdit();



        const dragStart = (e: L.LeafletEvent) => {
            if (this.editablePlotsLayer) {
                this.editablePlotsLayer.remove();
            }
        };

        const dragEnd = (e: L.LeafletEvent) => {
            const target = e.target;
            const startPos = turf.center(this.editablePlotsTurf);
            const endPos = turf.center(target.toGeoJSON());
            this.editablePlotsTurf = turf.transformTranslate(this.editablePlotsTurf,
                turf.distance(startPos, endPos),
                turf.bearing(startPos, endPos));

            // When the dragging ends, redraw the editablePlotsLayer
            this.editablePlotsLayer = L.featureGroup(this.editablePlotsTurf.features.map((o) => {
                const polygon = convertTurfToPolygon(turf.transformScale(o, 1));
                updateStylePolygon(polygon, 'PLOT', 'DEFAULT');
                return polygon;
            }));

            if (this.parentBlock) {
                const draggedPolygonTurf = turf.convex(this.editablePlotsLayer.toGeoJSON() as any);
                const turfBoundingPolygon = convertPolygonToTurf(this.parentBlock.polygon);
                if (!turf.booleanWithin(draggedPolygonTurf, turfBoundingPolygon)) {
                    this.parentBlock.updateStyle('BOUNDARY_ERROR');
                    this.isOutsideBoundary = true;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                } else {
                    this.parentBlock.updateStyle('DEFAULT');
                    this.isOutsideBoundary = false;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                }
            }
            this.editablePlotsLayer.addTo(this.map).bringToBack();
        };

        const rotateStart = (e: L.LeafletEvent) => {
            if (this.editablePlotsLayer) {
                this.editablePlotsLayer.remove();
            }
        };

        const rotateEnd = (e: any) => {
            this.editablePlotsTurf = turf.transformRotate(this.editablePlotsTurf, turf.radiansToDegrees(e.rotation));

            // When the dragging ends, redraw the editablePlotsLayer
            this.editablePlotsLayer = L.featureGroup(this.editablePlotsTurf.features.map((o) => {
                const polygon = convertTurfToPolygon(turf.transformScale(o, 1));
                updateStylePolygon(polygon, 'PLOT', 'DEFAULT');
                return polygon;
            }));
            this.editablePlotsLayer.addTo(this.map).bringToBack();

            // Remove and disable transformation of the current editablePolygon, we will recreate editablePolygon later
            this.editablePolygon.transform.disable();
            this.editablePolygon.dragging.disable();
            this.editablePolygon.remove();

            // Recreate the editable polygon so that the rotation calculation is reset after the plot shapes are transformed.
            // rotation area is now based on the updated editablePlotsLayer
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            this.editablePolygon = createEditablePolygon(this.editablePlotsLayer);
            this.editablePolygon.addTo(this.map);
            updateStylePolygon(this.editablePolygon, 'STUDY', 'EDIT');
            this.editablePolygon.transform.enable({ scaling: false, rotation: true });
            this.editablePolygon.dragging.enable();

            if (this.parentBlock) {
                const rotatedPolygonTurf = turf.convex(this.editablePlotsLayer.toGeoJSON() as any);
                const turfBoundingPolygon = convertPolygonToTurf(this.parentBlock.polygon);
                if (!turf.booleanWithin(rotatedPolygonTurf, turfBoundingPolygon)) {
                    this.parentBlock.updateStyle('BOUNDARY_ERROR');
                    this.isOutsideBoundary = true;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                } else {
                    this.parentBlock.updateStyle('DEFAULT');
                    this.isOutsideBoundary = false;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                }
            }

        };

        const createEditablePolygon = (convexArea: L.FeatureGroup<any>) => {
            const editablePolygon = L.polygon(featureToL(turf.convex(convexArea.toGeoJSON() as any)));
            editablePolygon.transform = new L.Handler.PathTransform(editablePolygon);
            editablePolygon.dragging = new L.Handler.PathDrag(editablePolygon);
            editablePolygon.on('dragstart', dragStart);
            editablePolygon.on('dragend', dragEnd);
            editablePolygon.once('rotatestart', rotateStart);
            editablePolygon.once('rotateend', rotateEnd);
            return editablePolygon;
        };

        // The original polygon and plots drawing layer
        this.polygon.remove();
        this.plotsDrawingLayer.remove();

        // Create a temporary editable polygon
        this.editablePolygon = createEditablePolygon(this.plotsDrawingLayer);

        // Create a temporary editablePlots layer that will be used to draw the transformed shape.
        // Copy the shapes from the main plotsDrawingLayer
        this.editablePlotsLayer = L.featureGroup();
        this.plotsDrawingLayer.eachLayer((layer: L.Layer) => {
            const polygon = L.polygon((layer as L.Polygon).getLatLngs());
            updateStylePolygon(polygon, 'PLOT', 'EDIT');
            this.editablePlotsLayer.addLayer(polygon);
        });

        // Create a feature collection that will be used to track/stora the transformed shapes.
        this.editablePlotsTurf = turf.featureCollection(Array.from(this.plots.values()).map((plot: any) => convertPolygonToTurf(plot.polygon)));

        this.editablePlotsLayer.addTo(this.map);
        this.editablePolygon.addTo(this.map);
        this.editablePolygon.transform.enable({ scaling: false, rotation: true });
        this.editablePolygon.dragging.enable();

        this.isEditing = true;
        updateStylePolygon(this.editablePolygon, this.type, 'EDIT'); // Highlight selected polygon

    }

    delete(): void {

        this.fieldMapContext.beforeDelete(this).then(() => {
            this.reset();

            this.fieldMapContext.studiesMap.delete(this.id);

            // Remove the study from the parent block's studies array
            if (this.parentBlock) {
                const index = this.parentBlock.studies.indexOf(this);
                if (index > -1) {
                    this.parentBlock.studies.splice(index, 1);
                }
            }

            this.polygon.remove();

            // Remove the study polygon from the parent block's studiesDrawingLayer
            if (this.parentBlock && this.parentBlock.studiesDrawingLayer && this.polygon) {
                this.parentBlock.studiesDrawingLayer.removeLayer(this.polygon);
            }

            // Remove all plots associated with this study
            this.plots.forEach((plot) => {
                plot.delete();
            });

            // Clear the plots map
            this.plots.clear();

            // Remove the plotsDrawingLayer from the map
            if (this.plotsDrawingLayer) {
                this.plotsDrawingLayer.remove();
            }

            // Remove any editing layers if they exist
            if (this.editablePolygon) {
                this.editablePolygon.remove();
            }
            if (this.editablePlotsLayer) {
                this.editablePlotsLayer.remove();
            }

            // Clear any references to editing layers
            this.editablePolygon = null;
            this.editablePlotsLayer = null;
            this.editablePlotsTurf = null;
            this.originalPlotsDrawingLayer = null;

            // Remove event listeners
            this.removeListeners();

            // Clear references
            this.parentBlock = null;
            this.polygon = null;
            this.map = null;
            this.fieldMapContext = null;
        }, (error) => {
        });

    }

    detectOutsideBoundary() {
        if (this.parentBlock) {
            const draggedPolygonTurf = turf.convex(this.plotsDrawingLayer.toGeoJSON() as any);
            const turfBoundingPolygon = convertPolygonToTurf(this.parentBlock.polygon);
            if (!turf.booleanWithin(draggedPolygonTurf, turfBoundingPolygon)) {
                this.parentBlock.updateStyle('BOUNDARY_ERROR');
                this.isOutsideBoundary = true;
                this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                this.resize();
            } else {
                this.parentBlock.updateStyle('DEFAULT');
                this.isOutsideBoundary = false;
                this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
            }
        }
    }

    show() {
        if (!this.isVisible) {
            this.map.addLayer(this.polygon);
            this.map.addLayer(this.plotsDrawingLayer);
            this.isVisible = true;
        }
    }

    hide() {
        if (this.isVisible) {
            this.map.removeLayer(this.polygon);
            this.map.removeLayer(this.plotsDrawingLayer);
            this.isVisible = false;
        }
    }

    addListeners() {
        super.addListeners();
    }

    isEditable(): boolean {
        // Study shape is always not editable
        return false;
    }

    isResizable(): boolean {
        return !this.isOutsideBoundary && this.isVisible;
    }

    isRotatable(): boolean {
        return !this.isOutsideBoundary && this.isVisible;
    }

    isSaveable(): boolean {
        return this.isEditing && !this.isOutsideBoundary && this.isVisible;
    }

    isDeletable(): boolean {
        return !this.isOutsideBoundary && this.isVisible;
    }
}
